/**
 * Initialize DayJS and add all the plugins that we need.
 */
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isToday from "dayjs/plugin/isToday";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isBetween from "dayjs/plugin/isBetween";
import weekday from "dayjs/plugin/weekday";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import minMax from "dayjs/plugin/minMax";
import isoWeek from "dayjs/plugin/isoWeek";

dayjs.extend(customParseFormat);
dayjs.extend(isToday);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);
dayjs.extend(weekday);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(minMax);
dayjs.extend(isoWeek);

import("dayjs/locale/sl");
import("dayjs/locale/en");
import("dayjs/locale/de");
import("dayjs/locale/hr");
import("dayjs/locale/sr");
import("dayjs/locale/it");
import("dayjs/locale/es");
import("dayjs/locale/bs");
import("dayjs/locale/fr");

/**
 * This function is used to intercept all the requests and check if the token is expired or not.
 */
import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  isAxiosError,
} from "axios";
import { jwtDecode } from "jwt-decode";
import { i18n } from "@lingui/core";
import { addToast } from "@heroui/react";
import { t } from "@lingui/core/macro";
import { MAINTENANCE_RETURN_KEY } from "@/pages/Maintenance/constants";
import { axiosClient } from "@/lib/api-client/request-helpers";
import { isMaintenanceResponse } from "@/utils/api-error";
import { adoptReissuedToken, setAccessToken } from "@/utils/auth-token";

// Some iOS WebViews leave `localStorage` null, and the interceptors run on every request.
function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function removeStored(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Storage unavailable; nothing to clear.
  }
}

// Its own instance: the default `axios` carries the expired-token interceptor
// too, so the refresh would wait on itself and every request would hang.
const refreshClient = axios.create();

async function requestFreshAccessToken(): Promise<string | null> {
  const refreshToken = readStored("usr_refresh_token");

  if (!refreshToken) return null;

  const { data } = await refreshClient.post<{ accessToken: string }>(
    `${import.meta.env.VITE_BE_HOST}/user/auth/refresh`,
    { refreshToken },
  );

  return data.accessToken;
}

let pendingRefresh: Promise<string | null> | null = null;

async function handleExpiredToken(config: InternalAxiosRequestConfig) {
  const token = readStored("usr_token");

  if (!token) {
    return config;
  }

  const { exp } = jwtDecode(token);

  if (!exp) return config;

  const expirationTime = exp * 1000;

  if (new Date().getTime() <= expirationTime) {
    return config;
  }

  // An expired access token does not mean the session is over: it is the
  // shorter of the two clocks. A passkey session runs 7 days off a 1-day
  // access token (see server session-policy), so redeem the refresh token
  // before logging anyone out. Shared across concurrent requests so a burst
  // of expired calls triggers one refresh, not one each.
  pendingRefresh ??= requestFreshAccessToken()
    .catch(() => null)
    .finally(() => {
      pendingRefresh = null;
    });

  const accessToken = await pendingRefresh;

  if (accessToken) {
    setAccessToken(accessToken);
    config.headers.set("x-access-token", accessToken);
    return config;
  }

  sentryLog.debug("Logout user - token timeout", {
    expiredAt: new Date(expirationTime).toISOString(),
  });
  removeStored("usr_token");
  removeStored("usr_refresh_token");
  location.reload();

  return config;
}

let mfaReauthCallback: (() => void | Promise<void>) | null = null;
export const MFA_REAUTH_TRIGGERED_ERROR_MESSAGE = "MFA reauth triggered";

export function setMfaReauthCallback(
  callback: (() => void | Promise<void>) | null,
) {
  mfaReauthCallback = callback;
}

export function isMfaReauthTriggeredError(error: unknown): boolean {
  return (
    error instanceof Error &&
    error.message === MFA_REAUTH_TRIGGERED_ERROR_MESSAGE
  );
}

function handleResponseError(error: unknown) {
  if (isMaintenanceResponse(error)) {
    if (
      typeof window !== "undefined" &&
      !window.location.pathname.startsWith("/maintenance")
    ) {
      try {
        const returnTo =
          window.location.pathname +
          window.location.search +
          window.location.hash;
        window.sessionStorage.setItem(MAINTENANCE_RETURN_KEY, returnTo || "/");
      } catch (storageError) {
        console.warn(
          "Unable to persist maintenance return target",
          storageError,
        );
      }
      window.location.replace("/maintenance");
    }
  }

  if (isAxiosError(error) && error.response?.status === 498) {
    addToast({
      title: t`Seja je potekla`,
      description: t`Seja je potekla, ponovno se prijavite`,
      color: "danger",
    });
    removeStored("usr_token");
    location.reload();
  }

  const shouldTriggerMfaReauth =
    isAxiosError(error) &&
    ((error.response?.status === 401 &&
      error.response?.data?.code === "session_expired") ||
      (error.response?.status === 403 &&
        error.response?.data?.code === "mfa_required"));

  if (shouldTriggerMfaReauth) {
    sentryLog.info("MFA re-authentication required, triggering modal", {
      status: error.response?.status,
      code: error.response?.data?.code,
    });

    if (mfaReauthCallback) {
      void mfaReauthCallback();
      return Promise.reject(new Error(MFA_REAUTH_TRIGGERED_ERROR_MESSAGE));
    }

    addToast({
      title: t`Seja je potekla`,
      description: t`Seja je potekla, ponovno se prijavite`,
      color: "danger",
    });
    removeStored("usr_token");
    removeStored("usr_refresh_token");
    removeStored("mfa_preauth_token");
    location.reload();
  }

  return Promise.reject(error);
}

function registerAxiosInterceptors(client: AxiosInstance) {
  client.interceptors.request.use(handleExpiredToken, (error) =>
    Promise.reject(error),
  );
  client.interceptors.response.use(
    (response) => {
      adoptReissuedToken(response.headers);
      return response;
    },
    (error) => {
      // 403/498 still passed authentication, so they can carry a re-issued
      // token too. A 401 never does — verification failed.
      adoptReissuedToken(error?.response?.headers);
      return handleResponseError(error);
    },
  );
}

[axios, axiosClient].forEach((client) => registerAxiosInterceptors(client));

/**
 * Load default English translations and activate the locale.
 * Other locales will be loaded dynamically when needed.
 */
import { type Messages } from "@lingui/core";
import { messages as enMessages } from "../locales/en/messages.mjs";
import { sentryLog } from "./sentry-utils";

i18n.load({ en: enMessages as Messages });
i18n.activate("en");
